import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
// Components
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import HeaderContainer from "styles/components/headerContainer";
import TextBlock from "components/TextBlock";
// Styled Components - REUSE existing hero styles
import {
  StyledHeroImage,
  StyledHeroImageGridItem,
  StyledHeroSectionContainer,
  StyledHeroTextGridItem,
} from "../../styles/components/hero/index.styles";
import { StyledGridContainer, StyledButtonContainer } from "index.styles";
// Utils
import useMediaQueries from "utils/mediaQueries.utils";
import { Phone } from "@mui/icons-material";
// ! Uncomment to add animations (removed animations for now)
// import withSectionAnimation from "styles/animations/components/withSectionAnimation";
// import { fadeInVariants } from "styles/animations/sectionFadeIn.animations";

// const AnimatedStyledHeroSectionContainer = withSectionAnimation(StyledHeroSectionContainer, fadeInVariants);
const iconMapping = {
  PhoneIcon: Phone,
};

const DynamicHero = ({
  title,
  subtitle,
  description,
  textBlockContent,
  highlightText = [],
  highlightColor,
  textColor,
  cta,
  primaryCta,
  secondaryCta,
  image,
  reverse = false,
  id = "dynamic-hero-section",
  ...props
}) => {
  const theme = useTheme();
  const { isMd, isLg } = useMediaQueries();

  const renderContent = () => {
    // If textBlockContent is provided, render TextBlock component
    if (textBlockContent) {
      return <TextBlock {...textBlockContent} />;
    }

    // Fallback to description rendering
    if (typeof description === "string" && highlightText.length > 0) {
      let processedDescription = description;

      // Replace each highlight text with spans
      highlightText.forEach((text) => {
        const regex = new RegExp(`(${text})`, "gi");
        processedDescription = processedDescription.replace(regex, `<span class="accent-highlight">$1</span>`);
      });

      return (
        <MKTypography
          component="p"
          variant={isLg ? "body1" : "body2"}
          sx={{
            marginTop: "1rem",
            ".accent-highlight": {
              color: highlightColor || theme.palette.accent.main,
              fontWeight: "bold",
            },
          }}
          dangerouslySetInnerHTML={{ __html: processedDescription }}
        />
      );
    }

    if (Array.isArray(description)) {
      return description.map((paragraph, index) => (
        <MKTypography key={index} component="p" variant={isLg ? "body1" : "body2"} sx={{ marginTop: "1rem" }}>
          {paragraph}
        </MKTypography>
      ));
    }

    return (
      <MKTypography component="p" variant={isLg ? "body1" : "body2"} sx={{ marginTop: "1rem" }}>
        {description}
      </MKTypography>
    );
  };

  return (
    <StyledHeroSectionContainer component="section" id={id}>
      <StyledGridContainer container justifyContent={"space-between"} sx={{ flexDirection: reverse ? "row-reverse" : "row" }}>
        <StyledHeroTextGridItem item order={isMd ? "2" : "2"} xs={12} md={8} lg={6}>
          <HeaderContainer
            title={title}
            highlightText={highlightText}
            subtitle={subtitle}
            textColor={textColor || theme.palette.primary.main}
            highlightColor={highlightColor || theme.palette.accent.main}
            element="h1"
            variant="h1"
            cssProps={{ margin: "0rem 0rem 1rem 0rem !important" }}
          />

          {(description || textBlockContent) && renderContent()}

          {(cta || primaryCta || secondaryCta) && (
            <StyledButtonContainer sx={{ display: "flex", gap: "1rem", flexDirection: isLg ? "row" : "column" }}>
              {(primaryCta || cta) && (
                <MKButton
                  component={Link}
                  to={(primaryCta || cta).link}
                  target={(primaryCta || cta).external ? "_blank" : undefined}
                  rel={(primaryCta || cta).external ? "noopener noreferrer" : undefined}
                  variant="contained"
                  color="secondary"
                  size="large"
                  aria-label={(primaryCta || cta).ariaLabel || (primaryCta || cta).text}
                  fullWidth={isMd ? false : true}
                  sx={{ minWidth: isMd ? "160px" : "auto" }}
                >
                  {(primaryCta || cta).text}
                </MKButton>
              )}

              {secondaryCta && (
                <MKButton
                  component={Link}
                  to={secondaryCta.link}
                  target={secondaryCta.external ? "_blank" : undefined}
                  rel={secondaryCta.external ? "noopener noreferrer" : undefined}
                  variant="outlined"
                  color="secondary"
                  size="large"
                  aria-label={secondaryCta.ariaLabel || secondaryCta.text}
                  fullWidth={isMd ? false : true}
                  sx={{ minWidth: isMd ? "160px" : "auto" }}
                  startIcon={
                    secondaryCta.startIcon &&
                    iconMapping[secondaryCta.startIcon.iconName] &&
                    React.createElement(iconMapping[secondaryCta.startIcon.iconName], { sx: { fontSize: "1.25rem", color: "inherit !important" } })
                  }
                >
                  {secondaryCta.text}
                </MKButton>
              )}

              {(cta?.subtext || primaryCta?.subtext) && (
                <MKTypography
                  variant="body2"
                  component="p"
                  sx={{
                    marginTop: "1rem",
                    color: theme.palette.accent.main,
                    fontStyle: "italic",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  {cta?.subtext || primaryCta?.subtext}
                </MKTypography>
              )}
            </StyledButtonContainer>
          )}
        </StyledHeroTextGridItem>

        <StyledHeroImageGridItem item order={isMd ? "1" : "1"} xs={12} md={1} lg={5}>
          <StyledHeroImage
            component={"img"}
            height={1}
            width={1}
            src={image?.src || image}
            alt={image?.alt || "Hero image"}
            reverse={reverse}
            cssProps={{ maxWidth: "600px", maxHeight: "460px", marginLeft: isLg ? "3rem" : "0rem" }}
          />
        </StyledHeroImageGridItem>
      </StyledGridContainer>
    </StyledHeroSectionContainer>
  );
};

export default DynamicHero;
